import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Boxes, Calendar, CalendarClock, ChevronDown, ClipboardCheck, ClipboardList, FileText, LayoutDashboard, LayoutGrid, LogOut, Menu as MenuIcon, Monitor, Moon, MoreHorizontal, ScrollText, ShoppingCart, Store, Sun, TrendingUp, Undo2, User, Wallet, Wrench, X,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { brand } from '../assets/brand';
import { onForegroundMessage, isFcmConfigured } from '../lib/fcm';
import { ChatbotWidget } from './ChatbotWidget';
import { Seo } from '../lib/seo';
import { NotificationBell } from './NotificationBell';
import { MyWorkGreeting } from './MyWorkGreeting';
import { useWorklist } from '../hooks/useWorklist';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthRefresh } from '../hooks/useAuthRefresh';
import { useBadges } from '../hooks/useBadges';
import { navBadgeParts, badgeTotal, type BadgePart } from './navBadgeParts';
import { NavBadge } from './NavBadge';
import { FULFILLMENT_V2_ENABLED, RETURNS_LIFECYCLE_ENABLED } from '../lib/flags';
import {
  Avatar, CountBadge, Dropdown, IconButton, MenuDivider, MenuItem, MenuLabel, Tooltip, toast, cn,
} from '../ui';
import { useGsapPageEnter } from '../ui/motion';

/* ───────────────────────── Navigation model ─────────────────────────────
 * Mirrors the original AppShell exactly: each leaf carries the permission
 * code(s) that gate it and (optionally) a `roles` restriction; a group is
 * hidden when none of its children are visible. SUPER_ADMIN (wildcard) sees
 * everything. Only the rendering changed — the gating/badge logic is identical.
 * --------------------------------------------------------------------------*/
type NavLeaf = {
  key: string;
  label: string;
  /**
   * The ROUTE this entry stands for — the key active-highlighting matches `location.pathname`
   * against (see `activeKey`). It is a bare path and must stay one: a query string here would stop
   * the entry ever matching the page it opens.
   */
  to: string;
  /**
   * Where the link actually goes, when that is narrower than the route.
   *
   * A badged entry points at the SLICE holding the rows it counted rather than the register's
   * default view — see `navBadgeParts`. Falls back to `to`.
   */
  href?: string;
  icon?: ReactNode;
  badge?: number;
  /** What `badge` is made of, for the hover breakdown. The badge is the sum of exactly these. */
  parts?: BadgePart[];
  perm?: string | string[];
  roles?: string[];
};
type NavDivider = { divider: true };
type NavChild = NavLeaf | NavDivider;
type NavGroup = {
  key: string;
  label: string;
  icon?: ReactNode;
  to?: string;
  /** See `NavLeaf.href` — the badge's own slice, when this group is a leaf that carries one. */
  href?: string;
  perm?: string | string[];
  roles?: string[];
  badge?: number;
  parts?: BadgePart[];
  children?: NavChild[];
};

const isDivider = (c: NavChild): c is NavDivider => 'divider' in c;

export function AppShellModern() {
  useAuthRefresh(); // keep role/permissions live without a full re-login
  const badges = useBadges(); // live "active work" counts (polled), permission-gated server-side
  // The same poll the greeting uses (react-query dedupes by key), read here only for the menu count.
  const { data: worklist } = useWorklist();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const isCustomer = useAuthStore((s) => s.isCustomer)();
  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const isDark = useThemeStore((s) => s.resolved)() === 'dark';

  const queryClient = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Which section is expanded in the mobile sheet. `null` = untouched (the section
  // holding the current page opens itself); `''` = the user collapsed everything.
  const [openSection, setOpenSection] = useState<string | null>(null);
  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
    setOpenSection(null);
  }, [location.pathname]);
  // Replays a subtle entrance on the content region each navigation.
  const pageRef = useGsapPageEnter<HTMLDivElement>([location.pathname]);

  // FCM foreground toast — kept on antd's imperative notification API (invisible
  // chrome, not a styling concern; a Tailwind toast system is a later step).
  useEffect(() => {
    if (!isFcmConfigured()) return;
    let dispose: (() => void) | undefined;
    onForegroundMessage((payload) => {
      const n = payload?.notification ?? {};
      toast(n.title ?? 'Green Bay Bio', { description: n.body ?? '' });
      // Refresh the bell feed immediately so the new notification + unread count appear.
      queryClient.invalidateQueries({ queryKey: ['notifications-feed'] });
      // A CHAT_MESSAGE push: refresh the chat list, the open thread's room and the launcher's unread
      // badge now rather than on the next poll (every chat query key starts with 'chat').
      if (payload?.data?.event === 'CHAT_MESSAGE' || String(payload?.data?.link ?? '').startsWith('/chat')) {
        queryClient.invalidateQueries({ queryKey: ['chat'] });
      }
    }).then((unsub) => { dispose = unsub; });
    return () => { dispose?.(); };
  }, [queryClient]);

  // Field staff (SP / SH) reach /wallets for THEIR OWN cash float, so the menu calls it
  // "My Wallet" for them; for everyone else (Admin / Accounts / Super Admin) it is the
  // team's register, "Field Staff Advances". A role test, not a permission one — Super Admin
  // holds '*' and would otherwise be labelled as if they carried a wallet.
  const myRoleCodes = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
  const isFieldStaff = myRoleCodes.includes('SALES_PERSON') || myRoleCodes.includes('SALES_HEAD');

  // ───────── Menu data (identical routes / perms / roles / badges) ─────────
  /*
   * WHAT EACH BADGE IS MADE OF, AND WHERE ITS ROWS ARE.
   *
   * The menu used to write every badge out as a sum of raw counters - `anomalies + regularizations +
   * incomplete`, four transfer counters, two wallet counters - which made the pill a number with no
   * stated meaning and no address. `navBadgeParts` declares each one as named parts carrying their
   * own slice, and the pill's value is the sum of exactly those, so the breakdown can never drift
   * from the number it explains.
   */
  const badgeParts = navBadgeParts(badges, {
    isWarehouseManager: myRoleCodes.includes('WAREHOUSE_MANAGER'),
    can: hasAnyPermission,
  });
  /**
   * Spread onto a badged menu entry: `{ badge, parts, href }`.
   *
   * `href` is set only when ONE queue is pending, and it is what makes the badge keep its promise:
   * the link lands on that queue's rows instead of a register whose default view may not hold a
   * single one of them. With several queues pending there is no one slice to open, so the entry
   * keeps its plain route and the page it opens has to break the number down itself.
   *
   * `to` is deliberately NOT touched - active-highlighting matches it against `location.pathname`,
   * and a query string there would stop an entry ever marking itself as the open page.
   */
  const badged = (key: string): { badge: number; parts: BadgePart[]; href?: string } => {
    const parts = badgeParts[key] ?? [];
    return { badge: badgeTotal(parts), parts, ...(parts.length === 1 ? { href: parts[0].to } : {}) };
  };
  const STAFF_MENU: NavGroup[] = [
    // The day's starting point: everything still waiting on this person, written as sentences.
    // First in the menu because it is the answer to "what do I do now?" — and ungated, like
    // /approvals, because the people who most need it (a rep, a storekeeper) hold no special
    // permission. The badge counts only the ACT rows: a number you cannot clear is just noise.
    {
      key: '/my-work', icon: <ClipboardList />, label: 'My Work', to: '/my-work',
      badge: worklist?.totalAct,
    },
    // Warehouse Manager home (warehouse-scoped). Only WHMs see it — the exec Dashboard group below
    // prunes out for them (its children all need DASH.* which a WHM lacks).
    {
      key: '/warehouse/dashboard', icon: <LayoutDashboard />, label: 'Dashboard', to: '/warehouse/dashboard',
      perm: ['WAREHOUSE.VIEW.ASSIGNED'], roles: ['WAREHOUSE_MANAGER'],
      ...badged('/warehouse/dashboard'),
    },
    {
      key: '/dashboard', icon: <LayoutDashboard />, label: 'Dashboard', to: '/dashboard',
      children: [
        { key: '/dashboard', label: 'Executive & Sales', to: '/dashboard', perm: ['DASH.EXECUTIVE', 'DASH.SALES'] },
        { key: '/dashboard/map', label: 'Map', to: '/dashboard/map', perm: 'DASH.MAP' },
        { key: '/people', label: 'Users', to: '/people', ...badged('/people'), perm: ['RETAILER.VIEW', 'FARMER.VIEW', 'USER.VIEW'] },
      ],
    },
    {
      key: 'masters', icon: <LayoutGrid />, label: 'Masters',
      children: [
        { key: '/products', label: 'Products', to: '/products', perm: 'PRODUCT.VIEW' },
        { divider: true },
        // Territories: Structure (hierarchy CRUD) + Coverage (assignments) are tabs of one page; badge = coverage gaps.
        { key: '/masters/territories', label: 'Territories', to: '/masters/territories', ...badged('/masters/territories'), perm: ['TERRITORY.VIEW', 'TERRITORY.ASSIGN', 'TERRITORY.ASSIGN.HEAD', 'TERRITORY.COVERAGE'] },
        { key: '/masters/tax', label: 'Tax (HSN)', to: '/masters/tax', perm: 'TAX_MASTER.VIEW' },
        // Sits beside the HSN rates because the two answer one question together: the rate comes from
        // the HSN, the HEAD (CGST+SGST vs IGST) comes from which registration supplies the warehouse.
        { key: '/masters/gst-registrations', label: 'GST Registrations', to: '/masters/gst-registrations', perm: ['GST.REGISTRATION.VIEW', 'GST.REGISTRATION.ADMIN'] },
        { key: '/masters/commercial-terms', label: 'Commercial Terms', to: '/masters/commercial-terms', perm: 'COMMERCIAL.VIEW' },
        // Reimbursement rates + daily-allowance slabs (effective-dated) — what field expenses pay back.
        { key: '/masters/expense-policy', label: 'Expense Policy', to: '/masters/expense-policy', perm: 'EXPENSE.POLICY.EDIT' },
      ],
    },
    {
      key: 'inv', icon: <Boxes />, label: 'Warehouse',
      children: [
        /*
         * The warehouse operations overview. A Warehouse Manager gets it as their own top-level
         * "Dashboard" above (it is their home), but that entry is `roles: ['WAREHOUSE_MANAGER']`, so
         * for everybody else the page existed with NOTHING anywhere linking to it — reachable only
         * by typing the URL. It belongs here, first in the group, because it is the overview you
         * open before the registers underneath it.
         *
         * Gated on WAREHOUSE.VIEW, which a Warehouse Manager does NOT hold: that is what keeps them
         * from seeing "Dashboard" twice, without needing an exclude-role mechanism the nav has no
         * concept of.
         */
        { key: '/warehouse/dashboard', label: 'Dashboard', to: '/warehouse/dashboard', perm: 'WAREHOUSE.VIEW' },
        { key: '/inventory/warehouses', label: 'Warehouses', to: '/inventory/warehouses', perm: 'WAREHOUSE.VIEW' },
        // Master Stock is now the stock-operations hub (Master Stock + Transfers + Adjustments tabs).
        // Perm = union of the three features so transfer/adjust-only users still reach it; its badge
        // sums all the warehouse pending work (transfers awaiting dispatch, receipt or a settled
        // discrepancy, plus adjustment approvals) — the same three-way split the tab names.
        { key: '/inventory/master-stock', label: 'Master Stock', to: '/inventory/master-stock', ...badged('/inventory/master-stock'), perm: ['STOCK.MASTER.ADD', 'STOCK.VIEW', 'STOCK.TRANSFER', 'STOCK.ADJUST.CREATE', 'STOCK.ADJUST.APPROVE'] },
        { key: '/inventory/stock', label: 'Stock & Valuation', to: '/inventory/stock', perm: 'STOCK.VIEW' },
        // Outbound dispatch register — WHM sees only their warehouse(s), Admin/SA see all (with a warehouse filter).
        { key: '/warehouse/dispatches', label: 'Dispatches', to: '/warehouse/dispatches', perm: ['WAREHOUSE.VIEW.ASSIGNED', 'WAREHOUSE.VIEW'] },
        // Transporters is now a TAB on the Warehouses page — the carriers that collect from those
        // locations. This standalone entry survives only for the roles that cannot open that page at
        // all: a Warehouse Manager and Accounts read the carrier list (TRANSPORTER.VIEW) but hold no
        // WAREHOUSE.VIEW, so without it they would lose the master they pack and reconcile against.
        { key: '/warehouse/transporters', label: 'Transporters', to: '/warehouse/transporters', perm: ['TRANSPORTER.VIEW', 'TRANSPORTER.ADMIN'], roles: ['WAREHOUSE_MANAGER', 'ACCOUNTS'] },
        // Non-sellable stock (expired · QC-hold · blocked · past-expiry) now lives as a tab on Stock & Valuation.
        // Admins reach Activity via the tab on the Warehouses page; a Warehouse Manager (no access to the
        // Warehouses page) keeps this standalone, warehouse-scoped entry.
        { key: '/warehouse/activity', label: 'Activity', to: '/warehouse/activity', perm: ['WAREHOUSE.VIEW.ASSIGNED', 'WAREHOUSE.VIEW'], roles: ['WAREHOUSE_MANAGER'] },
        // Admins reach Low Stock via the tab on the Warehouses page; a Warehouse Manager (who can't open
        // the Warehouses page) gets this standalone, warehouse-scoped entry.
        { key: '/inventory/low-stock', label: 'Low Stock', to: '/inventory/low-stock', perm: ['WAREHOUSE.VIEW.ASSIGNED', 'WAREHOUSE.VIEW'], roles: ['WAREHOUSE_MANAGER'] },
        { key: '/inventory/grn', label: 'GRN', to: '/inventory/grn', perm: 'GRN.VIEW' },
        { key: '/inventory/batch-trace', label: 'Batch Traceability', to: '/inventory/batch-trace', perm: 'STOCK.VALUATION.VIEW' },
      ],
    },
    {
      key: 'sales', icon: <ShoppingCart />, label: 'Sales',
      children: [
        // Someone who bills orders but does not fulfil them (Accounts) has ONE step on this page — uploading the Tally
        // invoice for packed goods — so their badge is that count, the page's "To invoice" card. Everyone else: open orders.
        {
          key: '/orders', label: 'Orders', to: '/orders', perm: 'ORDER.VIEW',
          ...badged('/orders'),
        },
        { key: '/orders/new', label: 'New Order', to: '/orders/new', perm: 'ORDER.CREATE' },
        { key: '/orders/approval', label: 'Approval Queue', to: '/orders/approval', ...badged('/orders/approval'), perm: 'ORDER.APPROVE' },
        // Fulfillment V2 · Phase 2.5 — persisted shortfall queue (planners resolve; a WHM sees their own, read-only).
        ...(FULFILLMENT_V2_ENABLED ? [{ key: '/orders/shortfalls', label: 'Shortfalls', to: '/orders/shortfalls', ...badged('/orders/shortfalls'), perm: ['ORDER.DISPATCH.PLAN', 'ORDER.EDIT', 'WAREHOUSE.PACK.START', 'WAREHOUSE.PACK.COMPLETE'] }] : []),
        { key: '/inquiries', label: 'Inquiries', to: '/inquiries', ...badged('/inquiries'), perm: 'INQUIRY.VIEW' },
      ],
    },
    {
      key: 'billing', icon: <FileText />, label: 'Billing',
      children: [
        { key: '/billing/invoices', label: 'Invoices', to: '/billing/invoices', ...badged('/billing/invoices'), perm: 'INVOICE.VIEW' },
        ...(RETURNS_LIFECYCLE_ENABLED ? [{ key: '/returns', label: 'Returns', to: '/returns', ...badged('/returns'), perm: 'RETURN.VIEW' }] : []),
        { key: '/billing/payments', label: 'Payments', to: '/billing/payments', ...badged('/billing/payments'), perm: 'PAYMENT.VIEW' },
        { key: '/billing/credit-notes', label: 'Credit Notes', to: '/billing/credit-notes', perm: 'INVOICE.VIEW' },
        { key: '/billing/aging', label: 'Aging', to: '/billing/aging', perm: 'OUTSTANDING.VIEW' },
      ],
    },
    {
      key: 'ffa', icon: <CalendarClock />, label: 'Field Force',
      children: [
        { key: '/ffa/my-day', label: 'My Field Day', to: '/ffa/my-day', perm: 'ATTENDANCE.SELF', roles: ['SALES_PERSON', 'SALES_HEAD'] },
        // The menu always opens the plain register, never the badge's review slice: following that link
        // dropped people on a pre-filtered "Needs review" list. The review days are one tile-click away.
        { key: '/ffa/attendance', label: 'Attendance', to: '/ffa/attendance', ...badged('/ffa/attendance'), href: undefined, perm: 'ATTENDANCE.VIEW' },
        // Beat Coverage is a set of TABS on this page, not a page of its own — the label names both
        // halves so the coverage side stays findable from the menu.
        { key: '/ffa/visits', label: 'Visits & Coverage', to: '/ffa/visits', perm: ['VISIT.VIEW', 'COVERAGE.VIEW'] },
        { key: '/ffa/feedback', label: 'Feedback', to: '/ffa/feedback', perm: 'FEEDBACK.VIEW' },
        { key: '/events', label: 'Events & Meets', to: '/events', ...badged('/events'), perm: 'EVENT.VIEW' },
        { divider: true },
        // Moved here from Dashboard / Billing: sales-person performance + their advances & expense claims.
        { key: '/performance', label: 'Sales Performance', to: '/performance', perm: 'DASH.SALES' },
        // Same route, two readings: own float for field staff, the team register for admins.
        { key: '/wallets', label: isFieldStaff ? 'My Wallet' : 'Field Staff Advances', to: '/wallets', ...badged('/wallets'), perm: 'EXPENSE.VIEW' },
        { key: '/expense-reports', label: 'Expense Reports', to: '/expense-reports', ...badged('/expense-reports'), perm: 'EXPENSE.VIEW' },
      ],
    },
    // Sits after Field Force, at the end of the operational groups it cuts across.
    //
    // NO permission gate. The page has two halves: the lanes you can APPROVE (which are themselves
    // permission-filtered, so a non-approver sees none) and the work SENT BACK TO YOU — and the
    // second half exists precisely for people who hold no approval permission at all. Gating the
    // menu item on being an approver would hide a returned order from the rep who has to fix it.
    // The badge is the sum: decisions waiting on you, plus your own work that came back.
    {
      key: '/approvals', icon: <ClipboardCheck />, label: 'Approvals', to: '/approvals',
      ...badged('/approvals'),
    },
    { key: '/complaints', icon: <Wrench />, label: 'Complaints', to: '/complaints', ...badged('/complaints'), perm: 'COMPLAINT.VIEW' },
    // Team chat has NO menu entry: it lives in the floating chat launcher (ChatbotWidget), whose FAB
    // carries the unread count. The /chat and /chat/:id routes stay — push deep links and the
    // launcher's "Open full screen" land there.
    {
      key: 'more', icon: <TrendingUp />, label: 'More',
      children: [
        { key: '/reports', label: 'Reports', to: '/reports', perm: 'REPORT.RUN' },
        // Both AI pages were routed in App.tsx but linked from NOWHERE — reachable only by someone
        // who already knew the URL to type. They sit beside Reports because that is what they are:
        // another way of asking the same data a question. Their own permissions still gate them.
        { key: '/ai/nl', label: 'Ask the Data (AI)', to: '/ai/nl', perm: 'AI.NL_ANALYTICS' },
        { key: '/ai/forecasts', label: 'Sales Forecast (AI)', to: '/ai/forecasts', perm: 'AI.FORECAST.SALES' },
        { divider: true },
        { key: '/notifications/broadcast', label: 'Send a Notification', to: '/notifications/broadcast', perm: 'NOTIF.BROADCAST' },
        { key: '/notifications/templates', label: 'Templates (FCM/SMS/Email)', to: '/notifications/templates', perm: 'NOTIF.TEMPLATE.ADMIN' },
        { key: '/notifications/wa-templates', label: 'WhatsApp Templates', to: '/notifications/wa-templates', perm: 'NOTIF.TEMPLATE.ADMIN' },
        { divider: true },
        { key: '/admin/banners', label: 'Landing Banners', to: '/admin/banners', perm: 'BANNER.MANAGE' },
        { key: '/admin/bank-details', label: 'Company & Billing Setup', to: '/admin/bank-details', perm: 'COMPANY.BANK.EDIT' },
        { key: '/admin/roles', label: 'Roles & Permissions', to: '/admin/roles', perm: 'ROLE.VIEW' },
        { key: '/admin/activity', label: 'Activity Log', to: '/admin/activity', perm: 'ACTIVITY.VIEW' },
        { key: '/admin/app-installs', label: 'App Installs', to: '/admin/app-installs', perm: 'APP_INSTALL.VIEW' },
      ],
    },
  ];

  const CUSTOMER_MENU: NavGroup[] = [
    // The day's starting point: everything still waiting on this person, written as sentences.
    // First in the menu because it is the answer to "what do I do now?" — and ungated, like
    // /approvals, because the people who most need it (a rep, a storekeeper) hold no special
    // permission. The badge counts only the ACT rows: a number you cannot clear is just noise.
    {
      key: '/my-work', icon: <ClipboardList />, label: 'My Work', to: '/my-work',
      badge: worklist?.totalAct,
    },
    // Retailer home — the dealer dashboard (dues/credit + order tracking + billing). Retailer-only:
    // farmers have no account/dashboard and land on /profile.
    { key: '/retailer/dashboard', icon: <LayoutDashboard />, label: 'Dashboard', to: '/retailer/dashboard', perm: 'OUTSTANDING.VIEW', roles: ['RETAILER'] },
    { key: '/orders', icon: <ShoppingCart />, label: 'My Orders', to: '/orders', ...badged('/orders'), perm: 'ORDER.VIEW' },
    { key: '/billing/invoices', icon: <FileText />, label: 'Invoices', to: '/billing/invoices', ...badged('/billing/invoices'), perm: 'INVOICE.VIEW' },
    ...(RETURNS_LIFECYCLE_ENABLED ? [{ key: '/returns', icon: <Undo2 />, label: 'Returns', to: '/returns', ...badged('/returns'), perm: 'RETURN.VIEW' }] : []),
    { key: '/account', icon: <Wallet />, label: 'My Account', to: '/account', perm: 'OUTSTANDING.VIEW', roles: ['RETAILER'] },
    { key: '/account/terms', icon: <ScrollText />, label: 'My Terms', to: '/account/terms', perm: 'OUTSTANDING.VIEW', roles: ['RETAILER'] },
    { key: '/events', icon: <Calendar />, label: 'Events', to: '/events', ...badged('/events'), perm: 'EVENT.VIEW' },
    { key: '/complaints', icon: <Wrench />, label: 'Complaints', to: '/complaints', ...badged('/complaints'), perm: 'COMPLAINT.VIEW' },
    { key: '/where-to-buy', icon: <Store />, label: 'Where to buy', to: '/where-to-buy', perm: 'FARMER.VIEW', roles: ['FARMER'] },
    { key: '/profile', icon: <User />, label: 'Profile', to: '/profile' },
  ];

  // ───────── Visibility + pruning (ported verbatim) ─────────
  const hasPerm = (perm?: string | string[]) =>
    hasAnyPermission(perm ? (Array.isArray(perm) ? perm : [perm]) : undefined);
  const myRoles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
  const roleOk = (roles?: string[]) => !roles || roles.some((r) => myRoles.includes(r));
  const leafVisible = (l: NavLeaf) => roleOk(l.roles) && hasPerm(l.perm);
  const groupSelfVisible = (g: NavGroup) => roleOk(g.roles) && hasPerm(g.perm);

  const pruneChildren = (children: NavChild[]): NavChild[] => {
    const out: NavChild[] = [];
    for (const c of children) {
      if (isDivider(c)) {
        if (out.length === 0 || isDivider(out[out.length - 1])) continue;
        out.push({ divider: true });
      } else if (leafVisible(c)) {
        out.push(c);
      }
    }
    while (out.length && isDivider(out[out.length - 1])) out.pop();
    return out;
  };

  const nav = isCustomer ? CUSTOMER_MENU : STAFF_MENU;

  // ───────── Active-key resolution (longest matching route prefix) ─────────
  const collectRoutes = (groups: NavGroup[]): string[] =>
    groups.flatMap((g) => [
      ...(g.to ? [g.to] : []),
      ...(g.children?.filter((c): c is NavLeaf => !isDivider(c)).map((c) => c.to) ?? []),
    ]);
  const activeKey = (() => {
    const p = location.pathname;
    const keys = collectRoutes(nav).filter((k) => k.startsWith('/'));
    let best = '';
    for (const k of keys) if ((p === k || p.startsWith(k + '/')) && k.length > best.length) best = k;
    if (!best && !isCustomer && (p.startsWith('/retailers') || p.startsWith('/farmers'))) return '/people';
    return best || (isCustomer ? '/orders' : '/dashboard');
  })();

  const groupRoutes = (g: NavGroup): string[] => [
    ...(g.to ? [g.to] : []),
    ...(g.children?.filter((c): c is NavLeaf => !isDivider(c)).map((c) => c.to) ?? []),
  ];
  const isGroupActive = (g: NavGroup) => groupRoutes(g).includes(activeKey);

  // ───────── What this user actually sees, and the badge each entry carries ─────────
  const visibleNav = nav.filter((g) => (g.children ? pruneChildren(g.children).length > 0 : groupSelfVisible(g)));
  const groupBadgeTotal = (g: NavGroup) =>
    g.children
      ? pruneChildren(g.children).reduce((s, c) => s + (isDivider(c) ? 0 : c.badge ?? 0), 0)
      : g.badge ?? 0;

  /* ───────── Adaptive "priority+" nav bar ─────────
   * The bar measures itself rather than trusting a breakpoint. How much room the menu
   * needs depends on the *user*, not the viewport: a Super Admin carries nine groups
   * with badges, a warehouse manager three — so the old fixed `xl` cut-off either let
   * the pills run under the user chip (~1280-1500px) or hid a half-empty bar on a
   * laptop. Whatever doesn't fit now folds, in order, into a trailing "⋯" menu.
   *
   * Widths come from an invisible ghost row: once an item is folded away it is no
   * longer in the bar, so it could never be re-measured from the live row. --------*/
  const navBarRef = useRef<HTMLDivElement>(null);
  const navGhostRef = useRef<HTMLDivElement>(null);
  const [fitCount, setFitCount] = useState<number | null>(null); // null → not measured yet
  // Re-measure when the menu itself changes shape (role/permission refresh, a badge
  // crossing into two digits, "My Wallet" ⇄ "Field Staff Advances").
  const navSignature = visibleNav.map((g) => `${g.key}#${g.label}#${groupBadgeTotal(g)}`).join('|');
  // badgeAfter[i] — would the "⋯" pill carry a count if the fold happened after item i?
  // A badged "⋯" is wider than a bare one, and reserving the wrong one is what lets a bar
  // "fit" and then overlap the user chip.
  const badgeAfter = visibleNav.map((_, i) => visibleNav.slice(i + 1).some((g) => groupBadgeTotal(g) > 0));

  useLayoutEffect(() => {
    const bar = navBarRef.current;
    const ghost = navGhostRef.current;
    if (!bar || !ghost) return;
    const GAP = 2; // matches gap-0.5 between pills
    const measure = () => {
      const avail = bar.clientWidth;
      if (!avail) return; // bar is display:none (small screens) — the hamburger owns the menu there
      const kids = Array.from(ghost.children) as HTMLElement[];
      if (kids.length < 3) return;
      // …the last two ghost entries are the bare and the badged "⋯" pill.
      const widths = kids.slice(0, -2).map((el) => el.getBoundingClientRect().width);
      const moreBare = kids[kids.length - 2].getBoundingClientRect().width;
      const moreBadged = kids[kids.length - 1].getBoundingClientRect().width;
      let used = 0;
      let n = 0;
      for (let i = 0; i < widths.length; i += 1) {
        const next = used + widths[i] + (i ? GAP : 0);
        // Every item but the last has to leave room for the "⋯" pill beside it.
        const reserve = i === widths.length - 1 ? 0 : (badgeAfter[i] ? moreBadged : moreBare) + GAP;
        if (next + reserve > avail) break;
        used = next;
        n += 1;
      }
      setFitCount(n);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [navSignature]);

  // ───────── Pill class helpers ─────────
  const pillBase =
    'inline-flex h-9 items-center gap-1.5 rounded-control px-3 type-body-medium transition-colors duration-150 whitespace-nowrap';
  // Note: nav items that have a route render as <a>, and Ant Design's global
  // anchor color would override these — so the text colors are forced (!) to win.
  const pillResting = 'text-content/70! hover:bg-brand-50 hover:text-brand-700! dark:hover:bg-brand-900/25';
  const pillActive = 'bg-brand-50 text-brand-700! font-semibold dark:bg-brand-900/30 dark:text-brand-300!';

  // Renders the desktop pill (Link if it has a route, else a button trigger).
  const renderPill = (g: NavGroup, opts: { active: boolean; total?: number; caret?: boolean; parts?: BadgePart[] }) => {
    const content = (
      <>
        <span>{g.label}</span>
        {/* A GROUP's pill carries the sum of its children and is not explained here: hovering it opens
            the dropdown, which lists those children with their own badges — that IS the breakdown, and a
            tooltip fighting the menu for the same hover would only hide it. A LEAF pill has no dropdown,
            so its number explains itself. */}
        {!!opts.total && <NavBadge parts={opts.parts} count={opts.total} className="ml-0.5 ring-0" />}
        {opts.caret && <ChevronDown size={14} className="opacity-60" />}
      </>
    );
    const cls = cn(pillBase, opts.active ? pillActive : pillResting);
    return g.to ? (
      <Link to={g.href ?? g.to} className={cls}>{content}</Link>
    ) : (
      <button type="button" className={cls}>{content}</button>
    );
  };

  // The trailing "⋯" pill holding whatever didn't fit (keeps its groups' badge total).
  const renderOverflowPill = (opts: { active: boolean; total: number }) => (
    <button
      type="button"
      aria-label="More sections"
      className={cn(pillBase, 'px-2.5', opts.active ? pillActive : pillResting)}
    >
      <MoreHorizontal size={18} />
      {!!opts.total && <CountBadge count={opts.total} className="ml-0.5 ring-0" />}
    </button>
  );

  // One top-level nav entry (desktop).
  const renderNavGroup = (g: NavGroup): ReactNode => {
    if (!g.children) {
      if (!groupSelfVisible(g)) return null;
      return <div key={g.key} className="shrink-0">{renderPill(g, { active: isGroupActive(g), total: g.badge, parts: g.parts })}</div>;
    }
    const kids = pruneChildren(g.children);
    if (!kids.length) return null;
    const total = kids.reduce((s, c) => s + (isDivider(c) ? 0 : c.badge ?? 0), 0);
    const active = isGroupActive(g);
    return (
      <Dropdown
        key={g.key}
        className="shrink-0"
        trigger="hover"
        panel={kids.map((c, i) =>
          isDivider(c) ? (
            <MenuDivider key={`d${i}`} />
          ) : (
            <MenuItem
              key={c.key}
              to={c.href ?? c.to}
              active={c.to === activeKey}
              icon={c.icon}
              trailing={<NavBadge parts={c.parts} count={c.badge} />}
            >
              {c.label}
            </MenuItem>
          ),
        )}
      >
        {renderPill(g, { active, total, caret: true })}
      </Dropdown>
    );
  };

  // Flat vertical version of a group (label + every child listed) — used by the desktop
  // "⋯" panel, where a hover menu should show its contents outright.
  const renderFlatGroup = (g: NavGroup): ReactNode => {
    if (!g.children) {
      if (!groupSelfVisible(g)) return null;
      return (
        <MenuItem key={g.key} to={g.href ?? g.to} icon={g.icon} active={isGroupActive(g)} trailing={<NavBadge parts={g.parts} count={g.badge} />}>
          {g.label}
        </MenuItem>
      );
    }
    const kids = pruneChildren(g.children);
    if (!kids.length) return null;
    return (
      <div key={g.key} className="py-1">
        <MenuLabel>{g.label}</MenuLabel>
        {kids.map((c, i) =>
          isDivider(c) ? (
            <MenuDivider key={`d${i}`} />
          ) : (
            <MenuItem key={c.key} to={c.href ?? c.to} active={c.to === activeKey} trailing={<NavBadge parts={c.parts} count={c.badge} />}>
              {c.label}
            </MenuItem>
          ),
        )}
      </div>
    );
  };

  /* Mobile sheet section. The flat version of this menu is 40+ rows — a long scroll on a
     phone — so each group collapses to one row and only the group holding the current
     page starts open. (The desktop "⋯" panel keeps the flat form: it is a hover menu.) */
  const activeGroupKey = visibleNav.find(isGroupActive)?.key ?? null;
  const expandedSection = openSection ?? activeGroupKey;

  /* Every badge in the sheet lines up on ONE right edge. A group row ends with a chevron, so a row
     WITHOUT one — a leaf group like Approvals, or any child link — has to reserve that same slot;
     otherwise its badge sits 26px further right than its neighbours' and the count column zig-zags
     down the menu (measured at 470px: chevron rows ended at 416, chevron-less rows at 442). */
  const sheetTrailing = (badge?: number, parts?: BadgePart[]): ReactNode => (
    <span className="flex items-center gap-2.5">
      <NavBadge parts={parts} count={badge} />
      <span className="w-4 shrink-0" aria-hidden />
    </span>
  );

  const renderSheetGroup = (g: NavGroup): ReactNode => {
    if (!g.children) {
      return (
        <MenuItem key={g.key} to={g.href ?? g.to} icon={g.icon} active={isGroupActive(g)} trailing={sheetTrailing(g.badge, g.parts)}>
          {g.label}
        </MenuItem>
      );
    }
    const kids = pruneChildren(g.children);
    const open = expandedSection === g.key;
    return (
      <div key={g.key}>
        <button
          type="button"
          aria-expanded={open}
          // The sheet closes on click (so tapping a link dismisses it) — a section
          // header must not bubble into that.
          onClick={(e) => { e.stopPropagation(); setOpenSection(open ? '' : g.key); }}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-control px-2.5 py-2.5 type-body-medium transition-colors',
            open ? 'bg-surface-2 text-content' : 'text-content hover:bg-surface-2',
          )}
        >
          <span className="grid h-4 w-4 shrink-0 place-items-center opacity-80 [&>svg]:size-4">{g.icon}</span>
          <span className="flex-1 text-left">{g.label}</span>
          <CountBadge count={groupBadgeTotal(g)} />
          <ChevronDown size={16} className={cn('shrink-0 text-subtle transition-transform duration-150', open && 'rotate-180')} />
        </button>
        {open && (
          <div className="mb-1 ml-4 border-l border-line pl-2 animate-pop-in">
            {kids.map((c, i) =>
              isDivider(c) ? (
                <MenuDivider key={`d${i}`} />
              ) : (
                <MenuItem key={c.key} to={c.href ?? c.to} active={c.to === activeKey} trailing={sheetTrailing(c.badge, c.parts)}>
                  {c.label}
                </MenuItem>
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  const themeOptions: { mode: 'light' | 'dark' | 'system'; label: string; icon: ReactNode }[] = [
    { mode: 'light', label: 'Light', icon: <Sun /> },
    { mode: 'dark', label: 'Dark', icon: <Moon /> },
    { mode: 'system', label: 'Follow system', icon: <Monitor /> },
  ];

  // Split the menu into what fits on the bar and what folds into the "⋯" menu.
  // Before the first measurement everything is rendered (but hidden) so the ghost row
  // and the bar are laid out in the same frame — no flash of a half-built bar.
  const shownGroups = fitCount === null ? visibleNav : visibleNav.slice(0, fitCount);
  const overflowGroups = fitCount === null ? [] : visibleNav.slice(fitCount);
  const overflowTotal = overflowGroups.reduce((sum, g) => sum + groupBadgeTotal(g), 0);
  const overflowActive = overflowGroups.some(isGroupActive);

  const headerIconBtn =
    'border border-line text-content/70 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20';

  return (
    <div className="flex min-h-screen flex-col">
      {/* The entire authenticated workspace is kept out of search engines. */}
      <Seo noindex title="Workspace" description="Green Bay Biotech internal workspace." />
      {/* ───────────────── Header — single full-width bar with a thin brand accent ───────────────── */}
      <header className="sticky top-0 z-[100] border-t-2 border-brand-600 border-b border-line bg-surface shadow-soft">
        <div className="flex h-16 items-center gap-2 px-3 sm:gap-3 sm:px-5 lg:gap-4 lg:px-6">
          {/* Brand */}
          <Link to="/" className="flex shrink-0 items-center">
            <img src="/logo.png" alt="Green Bay Biotech International" className="h-8 w-auto sm:h-10 lg:h-11" />
          </Link>

          {/* Desktop nav — width-measured, not breakpointed (see fitCount above) */}
          <div ref={navBarRef} className="relative hidden min-w-0 flex-1 items-center lg:flex">
            {/* Ghost row: the natural width of every pill plus both "⋯" variants. Invisible, so
                it is out of the tab order and takes no layout space, but still measurable.
                The clipping wrapper matters: `visibility:hidden` content still counts towards the
                document's scroll width, and without it the whole page scrolled sideways on a
                laptop-width screen. */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            <div
              ref={navGhostRef}
              className="invisible absolute left-0 top-0 flex flex-nowrap items-center gap-0.5"
            >
              {visibleNav.map((g) => (
                <div key={g.key} className="shrink-0">
                  {/* Same active state as the live pill — the active one is bolder, so
                      measuring every pill as "resting" under-reserves its width. */}
                  {renderPill(g, { active: isGroupActive(g), total: groupBadgeTotal(g), caret: !!g.children })}
                </div>
              ))}
              <div className="shrink-0">{renderOverflowPill({ active: false, total: 0 })}</div>
              {/* 999 renders as the widest badge form, "99+" — the worst case to reserve for. */}
              <div className="shrink-0">{renderOverflowPill({ active: false, total: 999 })}</div>
            </div>
            </div>

            <nav className={cn('flex items-center gap-0.5', fitCount === null && 'invisible')}>
              {shownGroups.map(renderNavGroup)}
              {overflowGroups.length > 0 && (
                <Dropdown
                  className="shrink-0"
                  trigger="hover"
                  align="end"
                  panelClassName="min-w-[248px] max-h-[calc(100vh-5.5rem)] overflow-y-auto"
                  panel={overflowGroups.map(renderFlatGroup)}
                >
                  {renderOverflowPill({ active: overflowActive, total: overflowTotal })}
                </Dropdown>
              )}
            </nav>
          </div>

          {/* spacer (below lg the hamburger owns the menu, so push the controls right) */}
          <div className="flex-1 lg:hidden" />

          {/* Right controls */}
          <div className="flex shrink-0 items-center gap-2">
            <Tooltip label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton aria-label="Toggle theme" className={headerIconBtn} onClick={toggleTheme}>
                {isDark ? <Sun /> : <Moon />}
              </IconButton>
            </Tooltip>
            <NotificationBell />

            {/* User menu */}
            <Dropdown
              trigger="click"
              align="end"
              panelClassName="min-w-[230px]"
              panel={
                <>
                  <MenuLabel>{user?.role}</MenuLabel>
                  <MenuDivider />
                  <MenuItem icon={<User />} onClick={() => navigate('/profile')}>My profile</MenuItem>
                  {/* No "Notification settings" entry: the page it opened is gone — which channel an
                      event goes out on is not a per-person setting any more. */}
                  <MenuDivider />
                  <MenuLabel>Theme</MenuLabel>
                  {themeOptions.map((t) => (
                    <MenuItem
                      key={t.mode}
                      icon={t.icon}
                      active={themeMode === t.mode}
                      onClick={() => setThemeMode(t.mode)}
                    >
                      {t.label}
                    </MenuItem>
                  ))}
                  <MenuDivider />
                  <MenuItem
                    icon={<LogOut />}
                    danger
                    onClick={() => { logout(); navigate('/'); }}
                  >
                    Logout
                  </MenuItem>
                </>
              }
            >
              <button
                type="button"
                // The name/role text is hidden below `sm`, which left the phone layout with an avatar-only
                // button that had no accessible name.
                aria-label={`Account menu${user?.fullName ? ` for ${user.fullName}` : ''}`}
                className="flex items-center gap-2.5 rounded-pill border border-line py-1 pl-1 pr-2.5 transition-colors hover:border-brand-300 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                <Avatar
                  size={30}
                  src={user?.profilePhotoUrl || undefined}
                  fallback={<User className="size-[18px]" />}
                  style={{ background: brand.harvest, color: brand.primaryDark }}
                />
                <span className="hidden max-w-[8.5rem] text-left leading-[1.15] sm:block xl:max-w-[12rem]">
                  <span className="block truncate type-body-strong text-content">{user?.fullName}</span>
                  <span className="block truncate text-2xs uppercase tracking-[0.4px] text-subtle">{user?.role}</span>
                </span>
                <ChevronDown size={14} className="text-subtle" />
              </button>
            </Dropdown>

            {/* Hamburger (mobile only) */}
            <IconButton
              aria-label="Menu"
              className={cn(headerIconBtn, 'lg:hidden')}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X /> : <MenuIcon />}
            </IconButton>
          </div>
        </div>

        {/* Mobile nav sheet */}
        {mobileOpen && (
          <>
            {/* Dim the page behind it so the sheet reads as a layer; tapping out closes. */}
            <div
              className="fixed inset-x-0 bottom-0 top-16 z-30 bg-black/30 lg:hidden dark:bg-black/50"
              onClick={() => setMobileOpen(false)}
            />
            <div className="absolute inset-x-0 top-full z-40 px-0 sm:px-5 lg:hidden">
              {/* Full-bleed on a phone; from sm up it hangs under the hamburger as a
                  card-width panel instead of a full-width slab. */}
              <div
                className={cn(
                  'ml-auto max-h-[calc(100vh-4.5rem)] w-full overflow-y-auto bg-surface p-2 shadow-pop animate-pop-in',
                  'border-b border-line sm:w-[22rem] sm:rounded-b-card sm:border-x',
                )}
                onClick={() => setMobileOpen(false)}
              >
                {visibleNav.map(renderSheetGroup)}
              </div>
            </div>
          </>
        )}
      </header>

      {/* ───────────────── Content — sits on the canvas; cards provide the surfaces ─────────────────
          pb clears the fixed chat launcher FAB — ChatbotWidget (56px @ right:24/bottom:24 → owns the bottom-right ~80px) so
          bottom-right page content (e.g. table pagination) is never hidden behind it. */}
      <main className="min-h-[280px] flex-1 p-4 pb-24 sm:p-6 sm:pb-24">
        {/* Subtle page transition on route change (fade + 8px lift). Keyed on the
            pathname via useGsapPageEnter; children are NOT remounted, so no page
            state is reset. Fully skipped under prefers-reduced-motion. */}
        <div ref={pageRef}>
          <Outlet />
        </div>
      </main>

      <footer className="border-t border-line px-4 py-4 text-center text-xs text-muted">
        Developed and Maintained by{' '}
        <a
          href="https://www.fibermax.co.in"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-brand-700 hover:underline dark:text-brand-400"
        >
          Fibermax Services Pvt Ltd
        </a>
      </footer>

      <ChatbotWidget />
      <MyWorkGreeting />
    </div>
  );
}
